import {
  curry,
  toString,
  take,
  equals,
  type,
} from 'ramda';
import { useEffect, useRef } from 'react';
import memoizeOne from 'memoize-one';
/* eslint-disable */
/**
 * Loggar och returnerar input
 *
 * `String -> a -> a`
 * @param {string} tag textsträng som appendas till input
 * @param {any} x objectet som ska loggas, toString körs på objectet innan det loggas
 * @return {any} returnerar x utan att ha rört det
 */
export const trace = curry((tag: string, x: any) => {
  console.log(`${tag}:`, toString(x));
  return x;
});

/**
 * Som trace, men kapar loggen till anget antal tecken
 *
 * `Number -> String -> a -> a`
 * @param {number} macChars antal tecken som loggen ska kapas till
 * @param {string} tag textsträng som appendas till input
 * @param {any} x objectet som ska loggas, toString körs på objectet innan det loggas
 * @return {any} returnerar x utan att ha rört det
 */
export const traceMaxChars = curry((maxChars: number, tag: string, x: any) => {
  console.log(`${tag}:\n`, take(maxChars, toString(x)));
  return x;
});

/**
 * som trace, men kapar loggen till 80 tecken
 *
 * `a -> a`
 */
export const traceS = traceMaxChars(80);

/**
 * som trace, men kapar loggen till 160 tecken
 *
 * `a -> a`
 */
export const traceM = traceMaxChars(160);

/**
 * som trace, men kapar loggen till 320 tecken
 *
 * `a -> a`
 */
export const traceL = traceMaxChars(320);

/**
 * Om man wrappar en reducer med traceReducer så loggar den
 * action och det nya statet för varje anrop.
 *
 * Förutsatt att man debuggar en reducer i taget så kan man
 * lägga till den direkt vid anropet till useReducer. Om man
 * vill debugga flera reducers samtidigt så får man ange en tag
 * för att se vilken reducer som loggas och lägga på traceReducer
 * vid definitionen av reducern så den inte muteras (det funkar
 * ändå men reducern körs då två gånger för varje action).
 */

type TraceReducerType = <A extends { type: string }, S extends object>(
  reducer: React.Reducer<S, A>,
  /** En tag som skrivs på loggen för att förtydliga vilken reducer det är om du loggar flera samtidigt */
  tag?: string,
  /** Något beräknat värde som ska loggas,  */
  customLog?: (action: A, formState: S) => any,
  customLogName?: string
) => React.Reducer<S, A>;

const getCurrentTimeFormatted = () => {
  const currentTime = new Date();
  const hours = currentTime.getHours();
  const minutes = currentTime.getMinutes();
  const seconds = currentTime.getSeconds();
  const milliseconds = currentTime.getMilliseconds();
  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};
const isObject = (val: unknown): val is Record<string, unknown> =>
  type(val) === 'Object';

const hasLeft = (val: unknown): val is { left: unknown; right?: unknown } =>
  isObject(val) && 'left' in val;

const hasRight = (val: unknown): val is { right: unknown; left?: unknown } =>
  isObject(val) && 'right' in val;

const hasBoth = (
  val: unknown
): val is { left: unknown; right: unknown } => hasLeft(val) && hasRight(val);

const equalValues = (val: unknown): boolean =>
  hasBoth(val) ? equals(val.left, val.right) : false;

const isEqual = (val: unknown): boolean => hasBoth(val) && equalValues(val);

const markAdded = (val: { left: unknown }) => [val.left, 'no new value'];
const markRemoved = (val: { right: unknown }) => ['no prev value', val.right];
const isAddition = (val: unknown): val is { left: unknown } =>
  hasLeft(val) && !hasRight(val);
const isRemoval = (val: unknown): val is { right: unknown } =>
  !hasLeft(val) && hasRight(val);

const rejectEqualObjects = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => !isEqual(value))
  );

const combineAsLeftAndRight = (
  leftObj: Record<string, unknown>,
  rightObj: Record<string, unknown>
): Record<string, { left?: unknown; right?: unknown }> => {
  const keys = new Set([...Object.keys(leftObj), ...Object.keys(rightObj)]);
  const result: Record<string, { left?: unknown; right?: unknown }> = {};

  keys.forEach(key => {
    result[key] = {
      ...(key in leftObj ? { left: leftObj[key] } : {}),
      ...(key in rightObj ? { right: rightObj[key] } : {}),
    };
  });

  return result;
};

export const objectDiff = (l: object, r: object): object => {
  const combined = combineAsLeftAndRight(
    l as Record<string, unknown>,
    r as Record<string, unknown>
  );
  const changedOnly = rejectEqualObjects(combined);

  return Object.fromEntries(
    Object.entries(changedOnly).map(([key, value]) => {
      if (isAddition(value)) {
        return [key, markAdded(value)];
      }
      if (isRemoval(value)) {
        return [key, markRemoved(value)];
      }
      if (
        hasBoth(value) &&
        isObject(value.left) &&
        isObject(value.right)
      ) {
        return [key, objectDiff(value.left, value.right)];
      }
      if (hasBoth(value)) {
        return [key, [value.left, value.right]];
      }
      return [key, value];
    })
  );
};

export const objectDiffByRef = (object1: any, object2: any) => {
  const allKeys = Object.keys({ ...object1, ...object2 });
  // Use this object to keep track of changed props
  const changesObj = {};
  // Iterate through keys
  allKeys.forEach(key => {
    // If previous is different from current
    if ((object1 as Record<string, unknown>)[key] !== (object2 as Record<string, unknown>)[key]) {
      // Add to changesObj
      (changesObj as Record<string, unknown>)[key] = {
        from: object1[key],
        _to_: object2[key],
      };
    }
  });
  return changesObj;
};

export const traceReducer: TraceReducerType = memoizeOne<TraceReducerType>(
  (reducer, tag: string = 'reducer', customLog, customLogName) =>
    (prevState, action) => {
      const newState = reducer(prevState, action);
      const changedState = objectDiff(prevState, newState);
      console.group(
        `${tag} %c${action?.['type']} %cat ${getCurrentTimeFormatted()}`,
        'color: black; font-weight: bold;',
        'color: black; font-weight: lighter;'
      );
      console.log(
        '%cPrevious State:',
        'color: #9E9E9E; font-weight: 700;',
        prevState
      );
      console.log('%cAction:', 'color: #00A7F7; font-weight: 700;', action);
      if (customLog) {
        console.log(
          `%c${customLogName ?? 'CustomLog'}:`,
          'color: #00A7F7; font-weight: 700;',
          customLog(action, prevState)
        );
      }
      console.log(
        '%cNext State:',
        'color: #47B04B; font-weight: 700;',
        newState
      );
      console.log(
        '%cChanged State:',
        'color: #47B04B; font-weight: 700;',
        changedState
      );
      console.groupEnd();
      return newState;
    }
);

export const useWhyDidYouUpdate = (name: string, props: Record<string, unknown>) => {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      // Use this object to keep track of changed props
      const changesObj: Record<string, unknown> = {};
      // Iterate through keys
      allKeys.forEach(key => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            _to_: props[key],
          };
        }
      });
      const changesObj2 = objectDiff(previousProps.current, props);
      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj2, changesObj);
      } else {
        console.log('[why-did-you-update]', name, 'update with equal props');
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props;
  });
};

// log that a component has been mounted
export const useLogMount = (name: string, props: object) => {
  useEffect(() => {
    console.log('[log-mount]', name, props);
  }, []);
};
